import { useCallback, useEffect, useMemo, useState } from 'react';
import Button from '@/components/base/Button';
import { formatDate } from '@/utils/format';
import { tokensInBody } from '@/utils/contract';
import {
  deleteContractTemplate,
  listContractTemplates,
  saveContractTemplate,
} from '@/utils/templateStore';
import type { ContractTemplate } from '@/types/portal';
import type { ContractDraft } from '../types';

interface ContractTemplatesProps {
  draft: ContractDraft;
  defaultName: string;
  sourceCampaign: string;
  onApply: (template: ContractTemplate) => void;
}

export default function ContractTemplates({
  draft,
  defaultName,
  sourceCampaign,
  onApply,
}: ContractTemplatesProps) {
  const [templates, setTemplates] = useState<ContractTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [appliedId, setAppliedId] = useState<string | null>(null);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [naming, setNaming] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [savedId, setSavedId] = useState<string | null>(null);

  const customCount = useMemo(
    () => templates.filter((template) => template.origin === 'custom').length,
    [templates],
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const list = await listContractTemplates();
      setTemplates(list);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const apply = (template: ContractTemplate) => {
    onApply(template);
    setPreviewId(null);
    setAppliedId(template.id);
    window.setTimeout(
      () => setAppliedId((current) => (current === template.id ? null : current)),
      2000,
    );
  };

  const startNaming = () => {
    setNameInput(defaultName);
    setNaming(true);
  };

  const confirmSave = async () => {
    const name = nameInput.trim() || defaultName;
    const created = await saveContractTemplate({
      name,
      fileName: draft.fileName,
      fileType: draft.fileType,
      fileSize: draft.fileSize,
      body: draft.body,
      sourceCampaign: sourceCampaign || undefined,
    });
    setNaming(false);
    await refresh();
    setSavedId(created.id);
    window.setTimeout(() => setSavedId((current) => (current === created.id ? null : current)), 2500);
  };

  const removeTemplate = async (id: string) => {
    await deleteContractTemplate(id);
    setConfirmId(null);
    await refresh();
  };

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-heading text-sm font-semibold text-foreground-950">
            Saved Contract Templates
          </h2>
          <p className="mt-0.5 text-xs text-foreground-500">
            Reuse a contract template you uploaded before — pick one and it fills the contract below
            with its wording and dynamic fields kept intact.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span className="text-[11px] font-label text-foreground-400">
            {customCount} of yours · {templates.length} total
          </span>
          <Button variant="outline" size="sm" icon="ri-add-line" onClick={startNaming}>
            Save current
          </Button>
        </div>
      </div>

      {naming ? (
        <div className="mt-3 flex flex-col gap-2 rounded-md border border-secondary-200 bg-secondary-50 p-3 sm:flex-row sm:items-center">
          <input
            value={nameInput}
            autoFocus
            maxLength={60}
            onChange={(event) => setNameInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                confirmSave();
              }
            }}
            placeholder="Name this contract template"
            className="min-w-0 flex-1 rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-400/30"
          />
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              icon="ri-bookmark-3-line"
              onClick={confirmSave}
              className="whitespace-nowrap"
            >
              Save contract template
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setNaming(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : null}

      {savedId ? (
        <p className="mt-3 flex items-center gap-1.5 rounded-md border border-accent-200 bg-accent-50 px-3 py-2 text-xs text-accent-800">
          <span className="w-3.5 h-3.5 flex items-center justify-center">
            <i className="ri-check-line" />
          </span>
          Saved to your contract templates — you can pick it on any future campaign.
        </p>
      ) : null}

      {loading ? (
        <p className="mt-3 text-xs text-foreground-400">Loading your contract templates…</p>
      ) : templates.length === 0 ? (
        <p className="mt-3 text-xs text-foreground-400">No contract templates yet.</p>
      ) : (
        <ul className="mt-3 space-y-2">
          {templates.map((template) => {
            const tokenCount = tokensInBody(template.body).length;
            const applied = appliedId === template.id;
            const isCustom = template.origin === 'custom';
            const isPreview = previewId === template.id;
            const isConfirming = confirmId === template.id;
            return (
              <li
                key={template.id}
                className="rounded-md border border-background-200 bg-background-100 px-3 py-2.5"
              >
                <div className="flex flex-wrap items-center gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700 text-base">
                    <i className={isCustom ? 'ri-bookmark-3-line' : 'ri-file-text-line'} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="flex flex-wrap items-center gap-1.5 truncate text-sm font-medium text-foreground-900">
                      {template.name}
                      <span
                        className={`rounded-sm px-1.5 py-0.5 text-[10px] font-label ${
                          isCustom
                            ? 'bg-accent-100 text-accent-800'
                            : 'bg-secondary-100 text-secondary-900'
                        }`}
                      >
                        {isCustom ? 'Yours' : 'Starter'}
                      </span>
                    </p>
                    <p className="truncate text-[11px] text-foreground-500">
                      {template.fileName
                        ? `${template.fileType} · ${template.fileSize}`
                        : 'Contract text'}
                      {' · '}
                      {tokenCount} dynamic field{tokenCount === 1 ? '' : 's'}
                      {isCustom ? ` · saved ${formatDate(template.savedAt.slice(0, 10))}` : ''}
                      {template.sourceCampaign ? ` · from ${template.sourceCampaign}` : ''}
                    </p>
                  </div>
                  <div className="flex shrink-0 flex-wrap items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => setPreviewId(isPreview ? null : template.id)}
                      className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-background-300 px-2 py-1 text-[11px] font-label text-foreground-600 hover:border-secondary-300 hover:text-foreground-800"
                    >
                      <i className={isPreview ? 'ri-arrow-up-s-line' : 'ri-eye-line'} />
                      {isPreview ? 'Hide' : 'Preview'}
                    </button>
                    <button
                      type="button"
                      onClick={() => apply(template)}
                      className={`inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border px-2.5 py-1 text-[11px] font-label transition-colors ${
                        applied
                          ? 'border-accent-300 bg-accent-50 text-accent-800'
                          : 'border-primary-300 bg-background-50 text-primary-700 hover:bg-primary-50'
                      }`}
                    >
                      <span className="w-3.5 h-3.5 flex items-center justify-center">
                        <i className={applied ? 'ri-check-line' : 'ri-arrow-down-line'} />
                      </span>
                      {applied ? 'Applied' : 'Use'}
                    </button>
                    {isCustom ? (
                      isConfirming ? (
                        <span className="inline-flex items-center gap-1 rounded-md border border-primary-200 bg-primary-50 px-2 py-1">
                          <span className="text-[11px] font-label text-primary-700">Remove?</span>
                          <button
                            type="button"
                            onClick={() => removeTemplate(template.id)}
                            aria-label="Confirm remove template"
                            className="inline-flex h-5 w-5 cursor-pointer items-center justify-center rounded-sm text-primary-700 hover:bg-primary-100"
                          >
                            <i className="ri-check-line" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfirmId(null)}
                            aria-label="Cancel remove template"
                            className="inline-flex h-5 w-5 cursor-pointer items-center justify-center rounded-sm text-foreground-500 hover:bg-background-200"
                          >
                            <i className="ri-close-line" />
                          </button>
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setConfirmId(template.id)}
                          aria-label={`Remove ${template.name}`}
                          className="inline-flex h-7 w-7 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-500 hover:border-primary-300 hover:text-primary-700"
                        >
                          <i className="ri-delete-bin-line" />
                        </button>
                      )
                    ) : null}
                  </div>
                </div>

                {isPreview ? (
                  <div className="mt-2 max-h-[180px] overflow-y-auto rounded-md border border-background-200 bg-background-50 px-3 py-2.5">
                    <pre className="whitespace-pre-wrap font-body text-[11px] leading-relaxed text-foreground-700">
                      {template.body}
                    </pre>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}